/**
 * Meals: the dated, counted record of a meal a stay is due.
 *
 * The grain is (reservation, service date, meal) with a count of `covers` — the
 * shape every PMS with a public schema converged on, and the reason this is not
 * a folio line. A four-night stay taking breakfast on two mornings is two rows,
 * and the rows say WHICH two; a guest who leaves at 07:00 still has a row
 * against that morning, so they stay on the kitchen's list until someone says
 * whether they were served. Counting covers for a date is then a lookup, not a
 * derivation from live in-house status that shifts under the host mid-service.
 *
 * Keyed on the RESERVATION, not the reservation unit. The assignment engine
 * moves stays between units, and a unit key would follow the room rather than
 * the people. `reservation_meal.place` carries the room-level fact instead.
 *
 * Two conventions here are borrowed rather than invented, because the industry
 * already settled them:
 *
 * - **The service date is not the night.** Breakfast on the morning of the 5th
 *   belongs to the night of the 4th. Apaleo makes `postNextDay` a required
 *   field, Mews calls it `ConsumptionMoment: NextTimeUnit`, protel "on the
 *   following day". `mealServiceDates()` is our version, and it is why a
 *   departure morning is a real, chargeable row rather than an edge case.
 * - **"Included" is two different things.** Under USALI a meal bundled into a
 *   package is allocated F&B revenue, but a meal simply free to every guest is
 *   `complimentary`: a Rooms-department expense with no revenue at all, and NOT
 *   deducted from the room rate. The classic guesthouse is the second one, so
 *   the inclusion enum is three-valued rather than a boolean.
 */

import { z } from "zod";
import {
  addDaysIso,
  nightsBetween,
  STAY_DAY_ZONE,
  toIsoDate,
} from "./iso-date";

// Which meal

/**
 * Only `breakfast` is surfaced today. The other two exist so half board and
 * full board — a real requirement in both launch markets — are a UI change
 * rather than a migration. `meal_kind` is a Postgres pgEnum, so adding a value
 * later needs `ALTER TYPE`.
 */
export const mealKindValues = ["breakfast", "lunch", "dinner"] as const;

export const MealKindSchema = z.enum(mealKindValues);
export type MealKind = z.infer<typeof MealKindSchema>;

// Where it is served

/**
 * Place of service. Not a mode of the morning: a per-row fact, because the
 * mixed case is the ordinary one. One room takes a tray upstairs on a dry
 * Tuesday; a Slovenian `sobodajalec` serves every room in the room always,
 * because the Pravilnik lets serving in the room stand in for having a room to
 * serve breakfast in. A whole-property switch is a bulk edit over these values,
 * never a separate field.
 *
 * Named for the classification standard rather than for us: Hotelstars scores
 * "breakfast area" (192, mandatory at every star level) separately from
 * "breakfast menu card via room service" (197), and Booking.com carries
 * `HAC 5000 "Breakfast in the room"` as a distinct amenity from `HAC 77
 * "Room service"`. In host-facing copy `in_room` reads "in the room".
 */
export const mealPlaceValues = [
  "breakfast_area",
  "in_room",
  "takeaway_box",
] as const;

export const MealPlaceSchema = z.enum(mealPlaceValues);
export type MealPlace = z.infer<typeof MealPlaceSchema>;

// How it is paid for

/**
 * Why three values and not a boolean, in USALI's terms:
 *
 * - `complimentary` — free to every guest. No F&B revenue exists; it is a Rooms
 *   expense, and explicitly not deducted from the room rate. Writes no folio
 *   line.
 * - `included` — sold inside a package (a Bed & Breakfast rate). Real revenue
 *   that should be allocated out of the room rate at market value. Writes a
 *   zero-balance folio line so the guest can see what they got.
 * - `charged` — bought separately. Writes a folio line that moves the balance.
 */
/**
 * Where a morning came from, which decides whether it is ours to change.
 *
 * `property` covers everything this property arranged: planned from the stay's
 * board, added by a host at the desk, or chosen by the guest in the portal. None
 * of those carry a commitment made outside the building, so any of them can be
 * dropped here.
 *
 * `channel` is a sale made AT the OTA. We did not take the money and cannot
 * amend the contract, so the guest may not cancel it from our stay page and a
 * channel revision, which is a full snapshot, must reconcile against it by
 * origin rather than by whether the date happens to be free.
 */
export const mealOriginValues = ["property", "channel"] as const;

export const MealOriginSchema = z.enum(mealOriginValues);
export type MealOrigin = z.infer<typeof MealOriginSchema>;

export const mealInclusionValues = [
  "complimentary",
  "included",
  "charged",
] as const;

export const MealInclusionSchema = z.enum(mealInclusionValues);
export type MealInclusion = z.infer<typeof MealInclusionSchema>;

// What happened to it

/**
 * `expected` is the number the kitchen preps against; `served` is what actually
 * happened; `skipped` is the guest who paid for breakfast and never came down.
 *
 * Deliberately three states rather than a served boolean: on a past morning,
 * a row still sitting at `expected` is an open question, and that is exactly
 * what the check-out blocker counts. A boolean would make "not served" and
 * "nobody has said yet" the same value, which is the ambiguity the whole
 * feature exists to remove.
 */
export const mealStatusValues = ["expected", "served", "skipped"] as const;

export const MealStatusSchema = z.enum(mealStatusValues);
export type MealStatus = z.infer<typeof MealStatusSchema>;

/** A past-dated row nobody has resolved: what check-out asks about. */
export function isUnconfirmed(status: MealStatus): boolean {
  return status === "expected";
}

// Board basis (what a stay or a rate plan sells with the room)

/**
 * What a stay or a rate plan includes, as the five values AlpineBits mandates.
 *
 * AlpineBits is the OpenTravel-derived standard used across Alpine Italy and
 * Austria — our exact market — and it deliberately does not use separate
 * breakfast/lunch/dinner booleans, because three booleans can express states
 * ("lunch but no breakfast") no rate plan means.
 */
export const boardBasisValues = [
  "room_only",
  "bed_and_breakfast",
  "half_board",
  "full_board",
  "all_inclusive",
] as const;

export const BoardBasisSchema = z.enum(boardBasisValues);
export type BoardBasis = z.infer<typeof BoardBasisSchema>;

/**
 * Channex `rate_plan.meal_type` wire values.
 *
 * Written out rather than cast, even though every value currently spells the
 * same as ours: the two vocabularies are independent, and Channex's own enum is
 * much wider (`buffet_breakfast`, `bermuda`, `self_catering`, and a dozen more)
 * because it enumerates OTA meal plans rather than the board bases a property
 * sells. A board basis we add later may have no Channex equivalent, and this
 * map is where that has to be decided rather than discovered on the wire.
 */
export const BOARD_BASIS_CHANNEX_MEAL_TYPE: Record<BoardBasis, string> = {
  all_inclusive: "all_inclusive",
  bed_and_breakfast: "bed_and_breakfast",
  full_board: "full_board",
  half_board: "half_board",
  room_only: "room_only",
};

/** Which meals a board basis entitles a guest to, per night of the stay. */
export const BOARD_BASIS_MEALS: Record<BoardBasis, readonly MealKind[]> = {
  all_inclusive: ["breakfast", "lunch", "dinner"],
  bed_and_breakfast: ["breakfast"],
  full_board: ["breakfast", "lunch", "dinner"],
  half_board: ["breakfast", "dinner"],
  room_only: [],
};

/** True when the plan entitles the guest to `meal` without a separate charge. */
export function boardIncludes(board: BoardBasis, meal: MealKind): boolean {
  return BOARD_BASIS_MEALS[board].includes(meal);
}

// Dates

/**
 * The service dates a stay is due `meal` on, given its nights.
 *
 * `nightDates` are the calendar days the guest sleeps — arrival day through the
 * day before departure. Breakfast falls the morning after each of them, so the
 * departure morning IS included and the arrival morning is not. That is the
 * whole of the arrival/departure asymmetry, expressed once.
 */
export function mealServiceDates(
  nightDates: readonly string[]
): readonly string[] {
  return nightDates.map((night) => addDaysIso(night, 1));
}

/**
 * The mornings a stay covers, in date order.
 *
 * The stay columns are day markers at midnight UTC (`STAY_DAY_ZONE`), so the
 * first night is that UTC date; projecting it through the property's zone
 * would date it to the previous evening anywhere west of Greenwich and shift
 * every morning it is due.
 */
export function stayMornings(
  checkInAt: Date | null,
  checkOutAt: Date | null
): readonly string[] {
  if (!(checkInAt && checkOutAt)) {
    return [];
  }
  const nights = nightsBetween(checkInAt, checkOutAt, STAY_DAY_ZONE);
  const firstNight = toIsoDate(checkInAt);
  return mealServiceDates(
    Array.from({ length: nights }, (_, i) => addDaysIso(firstNight, i))
  );
}

// The row

/**
 * A meal due on one date for one reservation.
 *
 * `covers` is how many people eat —
 * the F&B word, kept in the schema too because `count` is a SQL keyword and a
 * second name for the same number is a translation layer nobody needs. `place`
 * is where it is served; `requestedTime` is the wall-clock time the guest asked
 * for, which sorts an early riser to the top of the morning and is the field a
 * doorknob card would have carried.
 */
export const MealSchema = z.object({
  /**
   * The resolved total for this morning in minor units, not a per-cover price.
   *
   * A morning of two adults at 12 and one child at 6 has no per-cover figure
   * that reproduces the bill, and an average would be a number nobody agreed
   * to. Adult/child pricing is applied when the row is planned; what is stored
   * is what is owed. Zero for complimentary and included.
   */
  amountMinor: z.number().int().min(0),
  covers: z.number().int().min(1),
  inclusion: MealInclusionSchema,
  meal: MealKindSchema,
  place: MealPlaceSchema,
  /** 'HH:MM' wall clock, or null for "whenever service runs". */
  requestedTime: z.string().nullable().default(null),
  /** 'YYYY-MM-DD': the morning it is served, not the night it posts to. */
  serviceDate: z.string(),
  status: MealStatusSchema,
});
export type Meal = z.infer<typeof MealSchema>;

/**
 * Price one morning from the property's setup.
 *
 * Two flat rates against the counts the booking already carries. There is no
 * age threshold of our own on purpose: the channel has ALREADY decided who is a
 * child before it sends `children: 1` (Booking.com buckets 0-3 / 4-12 / 13-17,
 * Channex defaults 2-16), so a second definition here could only disagree with
 * the one the guest was quoted against.
 *
 * A property with no child rate charges everyone the adult price. Occupancy the
 * reservation never broke down counts as adults, which over-states rather than
 * under-charges.
 */
export function priceMeal(input: {
  adults: number;
  adultPriceMinor: number | null;
  children: number;
  childPriceMinor: number | null;
}): number {
  const adultRate = input.adultPriceMinor ?? 0;
  const childRate = input.childPriceMinor ?? adultRate;
  return input.adults * adultRate + input.children * childRate;
}

// Planning a stay's mornings

/** Per-property breakfast setup, as the planner needs it. */
export interface MealSetup {
  adultPriceMinor: number | null;
  childPriceMinor: number | null;
  currency: string | null;
  defaultInclusion: MealInclusion;
  defaultPlace: MealPlace;
  enabled: boolean;
}

export interface MealPlanRow {
  /** How the covers split, so a bill states who ate rather than only how many. */
  adultCovers: number;
  /** The rates applied, kept with the row so the bill cannot restate itself. */
  adultPriceMinor: number | null;
  amountMinor: number;
  childCovers: number;
  childPriceMinor: number | null;
  covers: number;
  currency: string | null;
  inclusion: MealInclusion;
  meal: MealKind;
  place: MealPlace;
  serviceDate: string;
}

export interface MealPlanInput {
  /** Occupancy. Null adults count as one — somebody is staying. */
  adults: number | null;
  /** The stay's board basis, or null when nothing stated one. */
  boardBasis: BoardBasis | null;
  checkInAt: Date | null;
  checkOutAt: Date | null;
  children: number | null;
  setup: MealSetup;
}

/** Only breakfast is surfaced today; the shape is ready for half board. */
export const PLANNED_MEALS: readonly MealKind[] = ["breakfast"];

/**
 * Which inclusion mode a stay's meal takes, or null when it is not due one.
 *
 * `complimentary` wins over the board basis. A property that gives breakfast to
 * every guest does not start charging because a stay's board happens to name it:
 * the two agree about what the guest gets and disagree only about whether
 * revenue exists, and the property's own answer is the true one.
 *
 * Returning null for an a-la-carte stay is the load-bearing case. Breakfast is
 * never invented — a `charged` row appears only when a host, a guest or a
 * channel actually adds one, because an auto-created charge is money nobody
 * agreed to and a phantom cover on the kitchen's list.
 */
export function resolveInclusion(
  board: BoardBasis | null,
  meal: MealKind,
  setup: MealSetup
): MealInclusion | null {
  if (!setup.enabled) {
    return null;
  }
  if (setup.defaultInclusion === "complimentary") {
    return "complimentary";
  }
  if (board && boardIncludes(board, meal)) {
    return "included";
  }
  return null;
}

/** How generous a board basis is, for picking the best of several plans. */
const BOARD_RANK: Record<BoardBasis, number> = {
  all_inclusive: 4,
  bed_and_breakfast: 1,
  full_board: 3,
  half_board: 2,
  room_only: 0,
};

/**
 * The most generous board basis among several plans.
 *
 * What a channel advertises for a room type sold on several plans, and what a
 * booking spanning two room types was sold: the guest was told they get
 * breakfast, whichever plan or room it came through.
 */
export function bestBoardBasis(
  candidates: readonly (BoardBasis | null)[]
): BoardBasis | null {
  const found = candidates.filter((b): b is BoardBasis => b !== null);
  if (found.length === 0) {
    return null;
  }
  return found.reduce((best, b) =>
    BOARD_RANK[b] > BOARD_RANK[best] ? b : best
  );
}

/**
 * The mornings a stay is due, priced. Pure: no database, no clock.
 *
 * Returns `[]` for a stay with no nights, no entitlement, or a property that
 * has not turned breakfast on.
 */
export function planMealsForStay(input: MealPlanInput): MealPlanRow[] {
  const { checkInAt, checkOutAt, setup } = input;
  const serviceDates = stayMornings(checkInAt, checkOutAt);
  if (serviceDates.length === 0) {
    return [];
  }

  // Occupancy a channel never broke down counts as adults: over-stating the
  // adult side never under-charges, and never hides a cover from the kitchen.
  const adults = input.adults ?? 1;
  const children = input.children ?? 0;
  const covers = adults + children;
  if (covers < 1) {
    return [];
  }

  const rows: MealPlanRow[] = [];
  for (const meal of PLANNED_MEALS) {
    const inclusion = resolveInclusion(input.boardBasis, meal, setup);
    if (!inclusion) {
      continue;
    }
    // Neither complimentary nor included puts money on the folio, so both are
    // stored at zero rather than at a price the guest is not being asked for.
    // Pricing an `included` breakfast here would bill it twice: once inside the
    // room rate it was sold in, and again as its own line.
    const amountMinor =
      inclusion === "charged"
        ? priceMeal({
            adultPriceMinor: setup.adultPriceMinor,
            adults,
            childPriceMinor: setup.childPriceMinor,
            children,
          })
        : 0;

    // Only a charged morning has rates worth keeping: an included one was sold
    // inside the room rate and a complimentary one was never priced, so a rate
    // card on either would describe a transaction that did not happen.
    const priced = inclusion === "charged" && setup.adultPriceMinor !== null;
    const adultRate = priced ? setup.adultPriceMinor : null;
    const childRate = priced
      ? (setup.childPriceMinor ?? setup.adultPriceMinor)
      : null;

    for (const serviceDate of serviceDates) {
      rows.push({
        adultCovers: adults,
        adultPriceMinor: adultRate,
        amountMinor,
        childCovers: children,
        childPriceMinor: childRate,
        covers,
        currency: setup.currency,
        inclusion,
        meal,
        place: setup.defaultPlace,
        serviceDate,
      });
    }
  }
  return rows;
}

// Reading what a channel sold

/**
 * Whether a provider's `price_mode` means "per person per night".
 *
 * The only distinction that changes a bill: everything else describes the whole
 * stay. Getting it backwards multiplies or divides the guest's bill by the
 * number of nights. Matched on the two words that carry the meaning, so
 * punctuation and case drift ("per person, per night") do not silently flip it.
 *
 * An absent mode reads as whole-stay, the conservative direction: multiplying
 * by nights on a guess inflates a bill, while not multiplying at worst
 * understates one, which a host can see and correct.
 */
export function isPerPersonPerNight(priceMode: string | undefined): boolean {
  if (!priceMode) {
    return false;
  }
  const mode = priceMode.toLowerCase();
  return mode.includes("person") && mode.includes("night");
}

/**
 * Split a stay-wide amount across the mornings it covers, in minor units.
 *
 * The remainder lands on the first morning so the parts sum EXACTLY to the
 * whole. Dividing and rounding each morning independently loses or invents
 * cents, and a folio that disagrees with the channel by a cent is a
 * reconciliation ticket somebody has to work.
 */
export function spreadAcrossMornings(
  totalMinor: number,
  mornings: number
): number[] {
  if (mornings < 1) {
    return [];
  }
  const per = Math.floor(totalMinor / mornings);
  const remainder = totalMinor - per * mornings;
  return Array.from(
    { length: mornings },
    (_, i) => per + (i === 0 ? remainder : 0)
  );
}
